/**
 * The chat text rule (ADR-068 decision 2), which the page applies before it sends a message and the server
 * applies again, since the page is not trusted. White space is trimmed at either end and nothing inside a
 * message is changed. A message is refused when nothing visible is left, when it runs past `MAX_CHAT_CHARS`
 * code points, or when it holds a character chat does not take: a C0 or C1 control or DEL (so a message is one
 * line), a line or paragraph separator, a bidirectional control (so no text draws backwards over a name), a
 * lone surrogate or a noncharacter. Every other character is kept, letters of every script, emoji, combining
 * marks and joiners included. The ranges are written out rather than read from the runtime's Unicode tables,
 * so the page and the server judge a message alike whatever version of Unicode either knows.
 */

/**
 * The most code points a chat message holds after trimming.
 */
export const MAX_CHAT_CHARS = 200;

/**
 * The most UTF-16 units a relayed message's text takes on the wire: the field tables count a string's length
 * in units, and a text of `MAX_CHAT_CHARS` code points takes at most twice as many.
 */
export const MAX_RELAYED_TEXT_UNITS = 2 * MAX_CHAT_CHARS;

/**
 * Why the rule refuses a message: nothing visible left, too long, or a character chat does not take.
 */
export type ChatTextProblem = "empty" | "long" | "characters";

/**
 * What the rule makes of a message: the text to relay, or why it is refused with the trimmed text's length in
 * code points, which a log line may carry in place of the text.
 */
export type ChatTextVerdict = { readonly kind: "ok"; readonly text: string } | { readonly kind: "refused"; readonly reason: ChatTextProblem; readonly chars: number };

/**
 * Whether a code point is one chat refuses: a C0 control, DEL or a C1 control; U+2028 or U+2029; a
 * bidirectional control; a surrogate, which a well-formed string holds only in pairs, so one met alone; or a
 * noncharacter.
 *
 * @param code The code point, or a lone surrogate's unit.
 * @returns True when the rule refuses it.
 */
function refused(code: number): boolean {
  if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) {
    return true;
  }
  if (code === 0x2028 || code === 0x2029) {
    return true;
  }
  if (code === 0x061c || code === 0x200e || code === 0x200f || (code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069)) {
    return true;
  }
  if (code >= 0xd800 && code <= 0xdfff) {
    return true;
  }
  return (code >= 0xfdd0 && code <= 0xfdef) || (code & 0xfffe) === 0xfffe;
}

/**
 * Whether a code point shows nothing: white space, or a zero-width space, joiner, non-joiner, word joiner or
 * zero-width no-break space. A message made only of these is empty.
 *
 * @param code The code point.
 * @returns True when it is blank.
 */
function blank(code: number): boolean {
  return code === 0x20 || code === 0xa0 || code === 0x1680 || (code >= 0x2000 && code <= 0x200d) || code === 0x202f || code === 0x205f || code === 0x2060 || code === 0x3000 || code === 0xfeff;
}

/**
 * The number of code points in a string: a surrogate pair counts once, and a lone surrogate once.
 *
 * @param text The text.
 * @returns The count.
 * @example
 * codePointCount("a😀");
 * // 2
 */
export function codePointCount(text: string): number {
  let count = 0;
  for (let i = 0; i < text.length; i += 1) {
    const unit = text.charCodeAt(i);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        i += 1;
      }
    }
    count += 1;
  }
  return count;
}

/**
 * Applies the chat text rule to a message as the player typed it or a client sent it.
 *
 * @param raw The message.
 * @returns The trimmed text to relay, or the reason it is refused, a refused character named before the
 *   length, with the trimmed text's length in code points.
 * @example
 * chatText("  hello  ");
 * // { kind: "ok", text: "hello" }
 * chatText("one\ntwo");
 * // { kind: "refused", reason: "characters", chars: 7 }
 */
export function chatText(raw: string): ChatTextVerdict {
  const text = raw.trim();
  let chars = 0;
  let characters = false;
  let visible = false;
  for (let i = 0; i < text.length; i += 1) {
    let code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      }
    }
    chars += 1;
    if (refused(code)) {
      characters = true;
    } else if (!blank(code)) {
      visible = true;
    }
  }
  if (characters) {
    return { kind: "refused", reason: "characters", chars };
  }
  if (!visible) {
    return { kind: "refused", reason: "empty", chars };
  }
  if (chars > MAX_CHAT_CHARS) {
    return { kind: "refused", reason: "long", chars };
  }
  return { kind: "ok", text };
}

/**
 * Why the chat text rule refuses a message, if it does.
 *
 * @param raw The message.
 * @returns The reason, or null when the message may be sent.
 */
export function chatTextProblem(raw: string): ChatTextProblem | null {
  const verdict = chatText(raw);
  return verdict.kind === "ok" ? null : verdict.reason;
}
